import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { ApiRouteRequestError, GenericClientError, PlatformError } from '~/utils/errors'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Account } from '@reward-platform/ancillaries-schemas/accounts'
import { getAccount } from '~/services/accountsService'
import { usePlatformTheme } from '~/context/theme'
import { useAviosAuthBalance } from '~/hooks/useAviosAuthBalance/useAviosAuthBalance'
import { useAuthContext, useAviosAuthEnabled } from '../auth'
import { useNotification } from '../notification'

export type AccountContextValue = {
  account?: Account
  householdAccount?: Account
  aviosBalance?: number
  householdAviosBalance?: number
  /** The balance available to spend: the household (TOTAL_REWARD) balance when the
   * member belongs to a household, otherwise the personal (REWARD) balance. */
  spendableAviosBalance?: number
  isLoading?: boolean
  isError?: boolean
  refetch?: () => Promise<void>
}

const ACCOUNT_KEY = 'getAccount'
const HOUSEHOLD_ACCOUNT_KEY = 'getHouseholdAccount'

export const AccountContext = createContext<AccountContextValue | undefined>(undefined)

export const AccountProvider = ({
  children,
}: PropsWithChildren): ReactElement<React.PropsWithChildren> => {
  const { isAuthenticated, userId } = useAuthContext()
  const queryClient = useQueryClient()
  const { addErrorCodeNotification } = useNotification()
  const { isAviosTheme } = usePlatformTheme()
  const aviosAuthEnabled = useAviosAuthEnabled()
  const { aviosAuthBalance, isAviosBalanceLoading, householdAuthBalance } = useAviosAuthBalance()
  const {
    data: account,
    isLoading,
    error: accountError,
    isError: isAccountError,
  } = useQuery<Account, ApiRouteRequestError>({
    queryKey: [ACCOUNT_KEY, userId],
    queryFn: () => getAccount('REWARD'),
    enabled: isAuthenticated && !aviosAuthEnabled,
  })

  useEffect(() => {
    if (isAccountError && accountError instanceof GenericClientError) {
      addErrorCodeNotification('ACCOUNT_CONTEXT_ERROR', {
        type: 'ERROR',
        action: 'RELOAD',
        errorCode: accountError.code,
        statusCode: accountError.httpStatus,
      })
    }
  }, [isAccountError, accountError, addErrorCodeNotification])

  const {
    data: householdAccount,
    isLoading: isLoadingHouseholdAccount,
    error: householdAccountError,
    isError: isHouseholdAccounError,
  } = useQuery<Account, ApiRouteRequestError>({
    queryKey: [HOUSEHOLD_ACCOUNT_KEY, userId],
    queryFn: () => getAccount('TOTAL_REWARD'),
    enabled: isAuthenticated && !aviosAuthEnabled,
  })

  useEffect(() => {
    if (
      isHouseholdAccounError &&
      householdAccountError instanceof GenericClientError &&
      !isAviosTheme
    ) {
      addErrorCodeNotification('HOUSEHOLD_ACCOUNT_CONTEXT_ERROR', {
        type: 'ERROR',
        action: 'RELOAD',
        errorCode: householdAccountError.code,
        statusCode: householdAccountError.httpStatus,
      })
    }
  }, [isAviosTheme, isHouseholdAccounError, householdAccountError, addErrorCodeNotification])

  const accountBalance = account?.balance.amount
  const householdBalance = householdAccount?.balance.amount

  const spendableBalance =
    accountBalance !== undefined || householdBalance !== undefined
      ? Math.max(accountBalance ?? 0, householdBalance ?? 0)
      : undefined

  const spendableAuthBalance =
    aviosAuthBalance !== undefined || householdAuthBalance !== undefined
      ? Math.max(aviosAuthBalance ?? 0, householdAuthBalance ?? 0)
      : undefined

  const contextValue = useMemo<AccountContextValue>(
    () => ({
      account,
      householdAccount,
      aviosBalance: aviosAuthEnabled ? aviosAuthBalance : accountBalance,
      householdAviosBalance: aviosAuthEnabled ? householdAuthBalance : householdBalance,
      spendableAviosBalance: aviosAuthEnabled ? spendableAuthBalance : spendableBalance,
      isLoading: isLoading || isLoadingHouseholdAccount || isAviosBalanceLoading,
      isError: !!isAccountError || !!isHouseholdAccounError,
      refetch: () =>
        queryClient.invalidateQueries({ queryKey: [ACCOUNT_KEY, HOUSEHOLD_ACCOUNT_KEY] }),
    }),
    [
      account,
      accountBalance,
      aviosAuthBalance,
      aviosAuthEnabled,
      householdAccount,
      householdBalance,
      spendableBalance,
      isAccountError,
      isAviosBalanceLoading,
      isHouseholdAccounError,
      isLoading,
      isLoadingHouseholdAccount,
      queryClient,
      householdAuthBalance,
      spendableAuthBalance,
    ]
  )

  return <AccountContext.Provider value={contextValue}>{children}</AccountContext.Provider>
}

export function useAccount(): AccountContextValue {
  const context = useContext(AccountContext)
  if (!context) {
    throw new PlatformError('useAccount must be used within a AccountProvider')
  }
  return context
}
